<template>
  <header class="header">
    <div class="container">
      <div class="header-content">
        <!-- 移动端菜单按钮 -->
        <div class="mobile-menu-btn visible-mobile" @click="toggleMenu">
          <i class="el-icon-menu"></i>
        </div>

        <!-- Logo -->
        <div class="logo" @click="goToHome">
          <img :src="`${$baseUrl}images/logo9.png`" alt="温故" class="logo-image" >
        </div>

        <!-- 桌面端导航菜单 -->
        <nav class="desktop-nav hidden-mobile">
          <router-link to="/" class="nav-link" :class="{ active: $route.name === 'Home' }">首页</router-link>
          <a href="#" @click.prevent="navigateToCategory('painting')" class="nav-link" :class="{ active: isPaintingActive }">绘画</a>
          <a href="#" @click.prevent="navigateToCategory('calligraphy')" class="nav-link" :class="{ active: isCalligraphyActive }">书法</a>
          <router-link to="/consult" class="nav-link" :class="{ active: $route.name === 'Consult' }">资讯</router-link>
          <router-link to="/about" class="nav-link" :class="{ active: $route.name === 'About' }">关于</router-link>
        </nav>

        <!-- 右侧功能区 -->
        <div class="header-right">
          <span class="date-info hidden-mobile">{{ lunarDateInfo }}</span>
          
          <!-- 登录功能开启时显示 -->
          <template v-if="showLoginFeature">
            <!-- 特殊IP用户：只显示昵称文字 -->
            <div v-if="isSpecialUser" class="special-user-name">
              <span>{{ userDisplayName }}</span>
            </div>
            
            <!-- 已登录：用户中心 -->
            <div v-else-if="isAuthenticated" class="user-center">
              <el-dropdown trigger="hover" @command="handleUserCommand">
                <span class="user-trigger">
                  <el-avatar 
                    v-if="currentUser && currentUser.avatar" 
                    :src="currentUser.avatar" 
                    :size="28"
                    class="user-avatar"
                  ></el-avatar>
                  <span class="user-name">{{ userDisplayName }}</span>
                  <i class="el-icon-arrow-down"></i>
                </span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="center">会员中心</el-dropdown-item>
                  <el-dropdown-item command="recharge">充值套餐</el-dropdown-item>
                  <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
            
            <!-- 未登录：登录注册按钮 -->
            <div v-else class="auth-buttons">
              <router-link to="/login" class="auth-btn login-register-btn">
                <span>登录/注册</span>
              </router-link>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- 移动端菜单 -->
    <div class="mobile-menu" :class="{ 'mobile-menu-open': menuVisible }" v-show="isMobile">
      <div class="mobile-menu-overlay" @click="closeMenu"></div>
      <div class="mobile-menu-content">
        <!-- 移动端菜单头部 -->
        <div class="mobile-menu-header">
          <div class="mobile-menu-logo">
            <img :src="`${$baseUrl}images/logo9.png`" alt="温故" class="logo-image">
          </div>
          <button class="mobile-menu-close" @click="closeMenu">
            <i class="el-icon-close"></i>
          </button>
        </div>
        
        <div class="mobile-menu-list">
          <!-- 登录功能开启时显示用户相关内容 -->
          <template v-if="showLoginFeature">
            <!-- 特殊IP用户：只显示昵称 -->
            <div v-if="isSpecialUser" class="mobile-special-user-section">
              <div class="mobile-special-user-info">
                <i class="el-icon-user-solid"></i>
                <span class="mobile-special-user-name">{{ userDisplayName }}</span>
              </div>
            </div>
            
            <!-- 已登录用户信息区域 -->
            <div v-else-if="isAuthenticated" class="mobile-user-section">
              <div class="mobile-user-info">
                <el-avatar 
                  v-if="currentUser && currentUser.avatar" 
                  :src="currentUser.avatar" 
                  :size="40"
                  class="mobile-user-avatar"
                ></el-avatar>
                <div class="mobile-user-details">
                  <div class="mobile-user-name">{{ userDisplayName }}</div>
                  <div class="mobile-user-mobile">{{ currentUser.mobile }}</div>
                </div>
              </div>
              <div class="mobile-user-actions">
                <a href="#" class="mobile-action-item" @click.prevent="handleUserCommand('center')">
                  <i class="el-icon-user"></i>
                  会员中心
                </a>
                <a href="#" class="mobile-action-item" @click.prevent="handleUserCommand('recharge')">
                  <i class="el-icon-star-off"></i>
                  充值套餐
                </a>
                <a href="#" class="mobile-action-item logout-action" @click.prevent="handleUserCommand('logout')">
                  <i class="el-icon-switch-button"></i>
                  退出登录
                </a>
              </div>
            </div>
            
            <!-- 未登录状态的登录注册按钮 -->
            <div v-else class="mobile-auth-section">
              <router-link to="/login" class="mobile-auth-btn login-register-btn" @click.native="closeMenu">
                <i class="el-icon-user"></i>
                登录/注册
              </router-link>
            </div>
          </template>
          
          <div class="mobile-nav-section">
            <h3>导航菜单</h3>
            <router-link to="/" class="mobile-nav-item" @click.native="closeMenu">首页</router-link>
            <a href="#" @click.prevent="navigateToCategory('painting')" class="mobile-nav-item">绘画</a>
            <a href="#" @click.prevent="navigateToCategory('calligraphy')" class="mobile-nav-item">书法</a>
            <router-link to="/consult" class="mobile-nav-item" @click.native="closeMenu">资讯</router-link>
            <router-link to="/about" class="mobile-nav-item" @click.native="closeMenu">关于</router-link>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { Lunar } from 'lunar-javascript'

export default {
  name: 'Header',
  data() {
    return {
      lunarDateInfo: '',
      dailyTimer: null,
      midnightTimer: null
    }
  },
  computed: {
    ...mapState(['menuVisible', 'isMobile', 'isSpecialUser', 'ipInfo']),
    
    // 判断绘画导航是否激活
    isPaintingActive() {
      return this.$route.name === 'ProductList' && 
             (this.$route.query.category === 'painting' || 
              (!this.$route.query.category && !this.$route.query.dynasty && !this.$route.query.subject))
    },
    
    // 判断书法导航是否激活
    isCalligraphyActive() {
      return this.$route.name === 'ProductList' && this.$route.query.category === 'calligraphy'
    },
    
    // 控制登录功能是否显示
    showLoginFeature() {
      console.log('showLoginFeature', process.env.VUE_APP_REQUIRE_LOGIN)
      // 如果环境变量中设置了显示登录功能，则显示
      // 这里可以根据需要调整逻辑，比如某些环境下完全隐藏登录功能
      return process.env.VUE_APP_REQUIRE_LOGIN !== 'false'
    },
    
    // 用户认证相关computed
    isAuthenticated() {
      // 如果是特殊IP用户，也视为已认证
      if (this.isSpecialUser) {
        return true
      }
      return this.showLoginFeature && this.$store.getters.isAuthenticated
    },
    
    currentUser() {
      // 如果是特殊IP用户，使用ipInfo中的数据
      if (this.isSpecialUser && this.ipInfo && this.ipInfo.data) {
        return {
          nickname: this.ipInfo.data.nickname || '特殊用户',
          avatar: this.ipInfo.data.avatar || null,
          mobile: this.ipInfo.data.mobile || ''
        }
      }
      return this.$store.getters.currentUser
    },
    
    userDisplayName() {
      // 如果是特殊IP用户，优先使用ipInfo中的nickname
      if (this.isSpecialUser && this.ipInfo && this.ipInfo.data) {
        return this.ipInfo.data.nickname || '特殊用户'
      }
      return this.$store.getters.userDisplayName
    }
  },
  mounted() {
    this.updateLunarDate()
    // 每天0点更新一次日期
    this.setDailyUpdate()
    // 初始化认证状态
    this.$store.dispatch('initAuth')
    
    // 初始化IP检测（用于特殊用户识别）
    this.$store.dispatch('checkIpLogin').then(ipInfo => {
      if (ipInfo && ipInfo.isSpecialUser) {
        console.log('检测到特殊IP用户:', ipInfo.data.nickname)
      }
    })
    
    // 监听路由变化，自动关闭移动端菜单
    // 只有在真正跳转到其他页面时才关闭菜单，同页面内的query参数变化不关闭
    this.$router.afterEach((to, from) => {
      // 如果路径发生变化（不同页面），则关闭菜单
      if (to.path !== from.path) {
        this.closeMenu()
      }
      // 如果只是query参数变化（同页面内的筛选切换），不关闭菜单
    })
  },
  methods: {
    ...mapActions(['toggleMenu', 'setMenuVisible']),
    
    handleUserCommand(command) {
      switch (command) {
        case 'center':
          // 如果已登录，跳转到会员中心，否则跳转到登录页面
          if (this.isAuthenticated) {
            this.safeNavigation('/user-center')
          } else {
            this.safeNavigation('/login')
          }
          break
        case 'collection':
          // 跳转到收藏页面
          this.safeNavigation('/user-center?tab=collection')
          break
        case 'recharge':
          // 跳转到充值页面
          this.safeNavigation('/recharge')
          break
        case 'logout':
          this.handleLogout()
          break
      }
    },
    
    // 处理退出登录
    handleLogout() {
      // 特殊IP用户不能退出登录
      if (this.isSpecialUser) {
        this.$message.warning('您当前是特殊用户，无需退出登录')
        this.closeMenu()
        return
      }
      
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 执行退出登录
        this.$store.dispatch('logout')
        this.$message.success('已退出登录')
        
        // 关闭移动端菜单
        this.closeMenu()
        
        // 如果当前在需要登录的页面，跳转到首页
        const authRequiredRoutes = ['/user', '/order', '/member']
        const currentPath = this.$route.path
        if (authRequiredRoutes.some(route => currentPath.startsWith(route))) {
          this.$router.push('/')
        }
      }).catch(() => {
        // 取消退出
      })
    },
    
    // 安全导航，避免重复导航警告
    safeNavigation(to) {
      // 将目标路由和当前路由进行比较
      const currentFullPath = this.$route.fullPath
      const targetPath = to.startsWith('/') ? to : `/${to}`
      
      // 如果目标路由和当前路由相同，则不进行导航
      if (currentFullPath !== targetPath) {
        this.$router.push(targetPath).catch(err => {
          // 捕获导航重复的错误，避免在控制台显示
          if (err.name !== 'NavigationDuplicated') {
            console.error('导航错误:', err)
          }
        })
      }
    },
    
    closeMenu() {
      this.setMenuVisible(false)
    },
    
    // 跳转到首页
    goToHome() {
      // 如果已经在首页，不进行跳转
      if (this.$route.path !== '/') {
        this.$router.push('/')
      }
      // 关闭移动端菜单
      this.closeMenu()
    },
    
    // 导航到分类页面（清除搜索参数）
    navigateToCategory(category) {
      console.log('🔗 Header: 导航到分类页面', category, '清除搜索参数')
      
      // 跳转到纯分类页面，只保留category参数
      this.$router.push({
        path: '/products',
        query: { category: category }
      }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('导航错误:', err)
        }
      })
      
      // 关闭移动端菜单
      this.closeMenu()
    },
    
    // 更新阴历日期信息
    updateLunarDate() {
      const today = new Date()
      const lunar = Lunar.fromDate(today)
      
      // 获取星期几
      const weekdays = ['日', '一', '二', '三', '四', '五', '六']
      const weekday = weekdays[today.getDay()]
      
      // 获取干支年
      const yearGanZhi = lunar.getYearInGanZhi() // 如：癸卯
      
      // 获取干支月
      const monthGanZhi = lunar.getMonthInGanZhi() // 如：乙巳
      
      // 获取干支日
      const dayGanZhi = lunar.getDayInGanZhi() // 如：辛日
      
      // 获取阴历月的中文表示（用于月份的传统表示）
      const lunarMonth = lunar.getMonth()
      const monthNames = ['正', '二', '三', '四', '五', '六', '七', '八', '九', '十', '冬', '腊']
      const monthName = monthNames[lunarMonth - 1] || '正'
      
      // 格式化为传统格式：星期三 癸卯年 乙巳腊月辛日
      this.lunarDateInfo = `星期${weekday} ${yearGanZhi}年 ${monthGanZhi}${monthName}月${dayGanZhi}`
    },
    
    // 设置每日更新
    setDailyUpdate() {
      const now = new Date()
      const tomorrow = new Date(now)
      tomorrow.setDate(tomorrow.getDate() + 1)
      tomorrow.setHours(0, 0, 0, 0)
      
      const timeUntilMidnight = tomorrow.getTime() - now.getTime()
      
      // 设置定时器在午夜更新
      this.midnightTimer = setTimeout(() => {
        this.updateLunarDate()
        // 然后每24小时更新一次
        this.dailyTimer = setInterval(() => {
          this.updateLunarDate()
        }, 24 * 60 * 60 * 1000)
      }, timeUntilMidnight)
    }
  },
  
  beforeDestroy() {
    // 清理定时器
    if (this.dailyTimer) {
      clearInterval(this.dailyTimer)
    }
    if (this.midnightTimer) {
      clearTimeout(this.midnightTimer)
    }
  }
}
</script>

<style lang="scss" scoped>
// 头部样式
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1001; // 确保header在banner图片之上
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  height: 60px;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    height: 50px;
  }
  
  .container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0 20px;
    
    @media (max-width: 768px) {
      padding: 0 15px;
    }
  }
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 10px;
  }
}

.mobile-menu-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  cursor: pointer;
  color: white;
  font-size: 20px;

  &:hover {
    color: var(--secondary-color);
  }
}

.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:active {
    transform: scale(0.95);
  }

  .logo-image {
    height: 40px;
    width: auto;
    object-fit: contain;
    display: block;

    @media (max-width: 768px) {
      height: 35px;
    }
  }

  .logo-text {
    font-size: 24px;
    font-weight: 700;
    color: white;
    font-family: 'KaiTi', serif;

    @media (max-width: 768px) {
      font-size: 20px;
    }
  }
}

// 桌面端导航
.desktop-nav {
  display: flex;
  align-items: center;
  gap: 30px;
  flex: 1;
  justify-content: center;

  .nav-link {
    color: white;
    font-size: 16px;
    font-weight: 500;
    text-decoration: none;
    padding: 8px 16px;
    border-radius: 4px;
    transition: all 0.3s ease;
    position: relative;

    &:hover {
      color: var(--secondary-color);
      background: rgba(255, 255, 255, 0.1);
    }

    &.active {
      color: var(--secondary-color);
      
      &::after {
        content: '';
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        width: 20px;
        height: 2px;
        background: var(--secondary-color);
      }
    }
  }
}

// 右侧功能区
.header-right {
  display: flex;
  align-items: center;
  gap: 15px;

  .date-info {
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;
    white-space: nowrap;
  }
}

.user-center {
  display: flex;
  align-items: center;
  height: 100%;
  
  .user-trigger {
    color: white;
    cursor: pointer;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
      color: var(--secondary-color);
    }
    
    .user-avatar {
      border: 2px solid rgba(255, 255, 255, 0.2);
    }
    
    .user-name {
      max-width: 100px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      
      @media (max-width: 768px) {
        display: none;
      }
    }
  }
}

// 特殊IP用户昵称显示
.special-user-name {
  display: flex;
  align-items: center;
  height: 100%;
  
  span {
    color: rgba(255, 255, 255, 0.9);
    font-size: 16px;
    font-weight: 500;
    white-space: nowrap;
    
    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
}

// 登录注册按钮
.auth-buttons {
  display: flex;
  align-items: center;
  height: 100%;
  
  .auth-btn {
    display: flex;
    align-items: center;
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    font-size: 16px;
    font-weight: normal;
    transition: all 0.3s ease;
    white-space: nowrap;
    
    @media (max-width: 768px) {
      font-size: 14px;
    }
    
    &:hover {
      color: var(--secondary-color);
    }
    
    &.login-register-btn {
      &:hover {
        color: var(--secondary-color);
      }
    }
  }
}


// 移动端菜单
.mobile-menu {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1001;
  visibility: hidden;
  opacity: 0;
  transition: all 0.3s ease;

  &.mobile-menu-open {
    visibility: visible;
    opacity: 1;
    
    .mobile-menu-content {
      transform: translateX(0);
    }
  }
  
  .mobile-menu-content {
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }
}

.mobile-menu-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
}

.mobile-menu-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 280px;
  height: 100%;
  background: white;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

// 移动端菜单头部
.mobile-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  background: var(--primary-color);
  border-bottom: 1px solid #e0e0e0;
  
  .mobile-menu-logo {
    display: flex;
    align-items: center;
    
    .logo-image {
      height: 30px;
      width: auto;
      object-fit: contain;
    }
  }
  
  .mobile-menu-close {
    background: none;
    border: none;
    color: white;
    font-size: 20px;
    cursor: pointer;
    padding: 5px;
    border-radius: 50%;
    width: 35px;
    height: 35px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      transform: rotate(90deg);
    }
    
    &:active {
      transform: rotate(90deg) scale(0.95);
    }
  }
}

.mobile-menu-list {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  
  .mobile-nav-section {
    margin-bottom: 30px;

    h3 {
      font-size: 16px;
      font-weight: 600;
      color: #333333;
      margin-bottom: 15px;
      padding-bottom: 8px;
      border-bottom: 1px solid #e0e0e0;
    }
  }

  .mobile-nav-item {
    display: block;
    padding: 12px 0;
    color: #333333;
    text-decoration: none;
    font-size: 15px;
    border-bottom: 1px solid #f0f0f0;
    transition: color 0.3s ease;

    &:hover {
      color: #8B4513;
    }

    &:last-child {
      border-bottom: none;
    }
  }
}

// 移动端用户信息区域
.mobile-user-section {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  
  .mobile-user-info {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 15px;
    
    .mobile-user-avatar {
      border: 2px solid #e0e0e0;
    }
    
    .mobile-user-details {
      flex: 1;
      
      .mobile-user-name {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 4px;
      }
      
      .mobile-user-mobile {
        font-size: 13px;
        color: #666;
      }
    }
  }
  
  .mobile-user-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    .mobile-action-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 0;
      color: #333;
      text-decoration: none;
      font-size: 14px;
      border-bottom: 1px solid #f0f0f0;
      transition: color 0.3s ease;
      
      i {
        font-size: 16px;
        width: 20px;
      }
      
      &:hover {
        color: var(--primary-color);
      }
      
      &.logout-action {
        color: #f56c6c;
        
        &:hover {
          color: #e6a23c;
        }
      }
      
      &:last-child {
        border-bottom: none;
      }
    }
  }
}

// 移动端特殊用户显示
.mobile-special-user-section {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  
  .mobile-special-user-info {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 15px 20px;
    background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
    border-radius: 12px;
    
    i {
      font-size: 20px;
      color: white;
    }
    
    .mobile-special-user-name {
      font-size: 16px;
      font-weight: 600;
      color: white;
    }
  }
}

// 移动端登录注册按钮
.mobile-auth-section {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: center;
  
  .mobile-auth-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 15px 30px;
    border-radius: 25px;
    text-decoration: none;
    font-size: 16px;
    font-weight: 600;
    transition: all 0.3s ease;
    
    &.login-register-btn {
      background: var(--primary-color);
      color: white;
      border: 1px solid var(--primary-color);
      
      &:hover {
        background: var(--accent-color);
        border-color: var(--accent-color);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(139, 69, 19, 0.3);
      }
    }
  }
}
</style>
